import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const MAX_STORED_PATHS = 10;
const MAX_STRING_LENGTH = 200;
const ALLOWED_BASE_TABLE_KEYS = new Set([
    'page',
    'per_page',
    'sort',
    'dir',
    'search',
    'status',
    'ride_id',
    'type',
    'category',
    'date',
    'month',
    'year',
    'state',
    'hasError',
    'work_type',
    'priority',
    'progress_status',
    'roles',
    'tag',
    'hourly_rate_min',
    'hourly_rate_max',
    'is_parts_supplier',
]);

const normalizeValue = (value) => {
    if (value == null) return value;
    if (typeof value === 'string') return value.slice(0, MAX_STRING_LENGTH);
    if (typeof value === 'number' || typeof value === 'boolean') return value;
    if (Array.isArray(value)) {
        return value
            .filter(item => ['string', 'number', 'boolean'].includes(typeof item))
            .slice(0, 20)
            .map(item => (typeof item === 'string' ? item.slice(0, MAX_STRING_LENGTH) : item));
    }
    return undefined;
};

const sanitizeGroup = (group, allowedKeys) => {
    if (!group || typeof group !== 'object') return null;

    const output = {};
    Object.entries(group).forEach(([key, value]) => {
        if (allowedKeys && !allowedKeys.has(key)) return;
        const normalized = normalizeValue(value);
        if (normalized !== undefined) {
            output[key] = normalized;
        }
    });

    return Object.keys(output).length > 0 ? output : null;
};

const trimFiltersByPath = (filtersByPath = {}, lastPathname = '') => {
    const entries = Object.entries(filtersByPath);
    if (entries.length <= MAX_STORED_PATHS) return filtersByPath;

    const sorted = entries.sort((a, b) => (b[1]?.__updatedAt || 0) - (a[1]?.__updatedAt || 0));
    const kept = new Map();

    if (lastPathname && filtersByPath[lastPathname]) {
        kept.set(lastPathname, filtersByPath[lastPathname]);
    }

    for (const [pathname, record] of sorted) {
        if (kept.size >= MAX_STORED_PATHS) break;
        kept.set(pathname, record);
    }

    return Object.fromEntries(kept.entries());
};

const sanitizeRecord = (pathname, existingRecord = {}, incomingFilters = {}) => {
    const merged = {
        ...(existingRecord || {}),
        ...(incomingFilters || {}),
    };

    return {
        pathname,
        baseTable: sanitizeGroup(merged.baseTable, ALLOWED_BASE_TABLE_KEYS),
        customParams: sanitizeGroup(merged.customParams),
        __updatedAt: Date.now(),
    };
};

const quotaSafeStorage = {
    getItem: (name) => localStorage.getItem(name),
    setItem: (name, value) => {
        try {
            localStorage.setItem(name, value);
        } catch (error) {
            if (error?.name !== 'QuotaExceededError') {
                throw error;
            }

            try {
                const parsed = JSON.parse(value);
                const state = parsed?.state || {};
                const filtersByPath = state.filtersByPath || {};
                const lastPathname = state.lastPathname || '';
                const compactedFilters = trimFiltersByPath(filtersByPath, lastPathname);

                const compacted = {
                    ...parsed,
                    state: {
                        ...state,
                        filtersByPath: compactedFilters,
                    },
                };

                localStorage.setItem(name, JSON.stringify(compacted));
            } catch (retryError) {
                localStorage.removeItem(name);
            }
        }
    },
    removeItem: (name) => localStorage.removeItem(name),
};

// Default empty record shape
const emptyRecord = (pathname = '') => ({
    pathname,
    baseTable: null,        // { page, per_page, sort, dir, search, ... }
    customParams: null,     // page-level custom filters (e.g. work orders extra filters)
    __updatedAt: 0,
});

const useFilterStore = create(
    persist(
        (set, get) => ({
            // Map of filters by pathname
            filtersByPath: {},
            lastPathname: '',

            // Set (or merge) filters for a path
            setFilters: (pathname, filters) => {
                set(state => {
                    const { lastPathname } = state;
                    let filtersByPath = state.filtersByPath;
                    // Determine if new pathname is unrelated to previous (not same or parent/child)
                    const isRelated = lastPathname && (
                        pathname === lastPathname ||
                        pathname.startsWith(lastPathname + '/') ||
                        lastPathname.startsWith(pathname + '/')
                    );
                    if (!isRelated && lastPathname) {
                        // Clear all previous filters when navigating to an unrelated section
                        filtersByPath = {};
                    }
                    const nextRecord = sanitizeRecord(pathname, filtersByPath[pathname] || emptyRecord(pathname), filters);
                    const nextFiltersByPath = {
                        ...filtersByPath,
                        [pathname]: nextRecord,
                    };

                    return {
                        filtersByPath: trimFiltersByPath(nextFiltersByPath, pathname),
                        lastPathname: pathname
                    };
                });
                // const record = get().filtersByPath[pathname];
                // console.log('Filters set for', pathname, '(auto-cleared others if unrelated)', record);
            },

            // Get filters for an exact path only
            getFilters: (pathname) => {
                const { filtersByPath } = get();
                if (filtersByPath[pathname]) return filtersByPath[pathname];
                return emptyRecord();
            },

            // Clear filters for a specific path (or all if omitted)
            clearFilters: (pathname) => {
                if (!pathname) {
                    set({ filtersByPath: {} });
                    return;
                }
                set(state => {
                    const copy = { ...state.filtersByPath };
                    delete copy[pathname];
                    return { filtersByPath: copy };
                });
            },

            // Update a single field inside baseTable or customParams for a path
            updateFilter: (pathname, group, field, value) => {
                set(state => {
                    const existing = state.filtersByPath[pathname] || emptyRecord(pathname);
                    const targetGroup = existing[group] || {};
                    const normalizedValue = normalizeValue(value);
                    if (normalizedValue === undefined) {
                        return state;
                    }

                    const nextRecord = sanitizeRecord(pathname, existing, {
                        [group]: {
                            ...targetGroup,
                            [field]: normalizedValue,
                        },
                    });

                    const nextFiltersByPath = {
                        ...state.filtersByPath,
                        [pathname]: nextRecord,
                    };

                    return {
                        filtersByPath: trimFiltersByPath(nextFiltersByPath, state.lastPathname || pathname),
                    };
                });
            },

            // Migration utility (optional): promote old single currentFilters to new map
            migrateLegacy: () => {
                const state = get();
                if (state.currentFilters && state.currentFilters.pathname && !state.filtersByPath[state.currentFilters.pathname]) {
                    const { pathname, ...rest } = state.currentFilters;
                    set(state2 => ({
                        filtersByPath: {
                            ...state2.filtersByPath,
                            [pathname]: { pathname, baseTable: rest }
                        }
                    }));
                }
            }
        }),
        {
            name: 'base-table-filters',
            version: 3,
            storage: quotaSafeStorage,
            partialize: (state) => ({
                lastPathname: state.lastPathname,
                filtersByPath: trimFiltersByPath(state.filtersByPath, state.lastPathname),
            }),
        }
    )
);

export default useFilterStore;